/**
 * Service History API
 * Client-side API calls for vehicle service history
 */

import { api } from './api';

export type VehicleHistorySource = 'system' | 'manual' | 'case';

export interface ServiceType {
  id: string;
  name: string;
  nameEn: string | null;
  description: string | null;
  estimatedDurationMins: number;
  isActive: boolean;
}

export interface ServiceHistoryEntry {
  id: string;
  vehicleId: string;
  serviceTypeId: string;
  serviceTypeName?: string | null;
  performedAt: string | number | Date;
  cost?: number | null;
  technicianName?: string | null;
  notes?: string | null;
  partsUsed?: string | null;
  bookingId?: string | null;
  source: VehicleHistorySource;
  createdAt: string | number | Date;
  updatedAt: string | number | Date;
  meta?: {
    caseId?: string;
    caseStatus?: string;
    archivedAt?: number | null;
    bookingCount?: number;
    jobCount?: number;
  };
}

export interface CreateHistoryInput {
  serviceTypeId: string;
  performedAt: string;
  cost?: number;
  technicianName?: string;
  notes?: string;
  partsUsed?: string;
}

export interface UpdateHistoryInput {
  serviceTypeId?: string;
  performedAt?: string;
  cost?: number | null;
  technicianName?: string | null;
  notes?: string | null;
  partsUsed?: string | null;
}

/**
 * Get all service history entries for a vehicle
 */
export async function getVehicleHistory(vehicleId: string): Promise<ServiceHistoryEntry[]> {
  return api.get<ServiceHistoryEntry[]>(`/api/vehicles/${vehicleId}/history`);
}

/**
 * Get a single history entry
 */
export async function getHistoryEntry(
  vehicleId: string,
  entryId: string
): Promise<ServiceHistoryEntry> {
  return api.get<ServiceHistoryEntry>(`/api/vehicles/${vehicleId}/history/${entryId}`);
}

/**
 * Create a manual service history entry
 */
export async function createHistoryEntry(
  vehicleId: string,
  data: CreateHistoryInput
): Promise<ServiceHistoryEntry> {
  return api.post<ServiceHistoryEntry>(`/api/vehicles/${vehicleId}/history`, data);
}

/**
 * Update a service history entry
 */
export async function updateHistoryEntry(
  vehicleId: string,
  entryId: string,
  data: UpdateHistoryInput
): Promise<ServiceHistoryEntry> {
  return api.put<ServiceHistoryEntry>(`/api/vehicles/${vehicleId}/history/${entryId}`, data);
}

/**
 * Delete a service history entry
 */
export async function deleteHistoryEntry(vehicleId: string, entryId: string): Promise<void> {
  await api.delete(`/api/vehicles/${vehicleId}/history/${entryId}`);
}

/**
 * Get all active service types
 */
export async function getServiceTypes(): Promise<ServiceType[]> {
  return api.get<ServiceType[]>('/api/service-types');
}
